import { Button, Chip, Grid, InputAdornment, Stack, TextField } from '@mui/material';
import { debounce, isArray } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { Search, Sliders } from 'react-feather';
import { useNavigate, useSearchParams } from 'react-router-dom';
import IconFeather from 'src/components/common/Icon';
import { useQueryParams } from 'src/hooks/useQueryParam';
import { checkDataBeforeRendering } from 'src/utils/helpers/ui';
import { FormType, useFormSearch } from 'src/zustand/useFormSearch';

const FilterCompanyProfile = () => {
  const query = useQueryParams();
  const navigate = useNavigate();
  const { handleOpenSearch } = useFormSearch((state) => state);
  const [, setSearchParams] = useSearchParams();
  const [defaultQuery, setDefaultQuery] = useState(query?.company_name);

  useEffect(() => {
    setDefaultQuery(query?.company_name || '');
  }, [query?.company_name]);

  const onChangeSearch = useCallback(
    debounce(async (e) => {
      // TODO: Improve UI, Show advance search on screen
      // setSearchParams({ company_name: String(e.target.value), offset: '0', limit: '10' });
      // Keep data advance search when type Quick Search
      setSearchParams((searchParams) => {
        searchParams.set('company_name', String(e.target.value));
        searchParams.set('offset', '0');
        searchParams.set('limit', '10');
        return searchParams;
      });
    }, 1000),
    [query],
  );

  // const handleRedirectToComparePage = () => {
  //   let search = '';
  //   selectedCompanies.forEach((company) => (search += `companyIds=${company.id}&`));
  //   navigate({
  //     pathname: AuthRouteKeys.CompareCompany,
  //     search: search,
  //   });
  // };

  const renderSearchTags = () => {
    const onRemoveSearchTag = (field: string, value?: string) => {
      setSearchParams((searchParams) => {
        const currentValues = searchParams.getAll(field);
        //if we have many value with same field, just remove 1
        if (currentValues.length > 1 && field === 'jurisdiction') {
          const updatedValues = currentValues.filter((v) => v !== value);
          searchParams.delete(field);
          updatedValues.forEach((v) => {
            searchParams.append(field, v);
          });
        } else {
          searchParams.delete(field);
        }
        return searchParams;
      });
    };

    const renderStatus = () => {
      if (query.include_all_company_status === 'true') return <Chip label={'All Status'} size="small" />;
      else return <Chip label={'Active'} size="small" />;
    };

    const renderListJurisdictions = () => {
      if (isArray(query.jurisdiction))
        return query.jurisdiction.map((item: string) => (
          <Chip key={item} onDelete={() => onRemoveSearchTag('jurisdiction', item)} label={item} size="small" />
        ));
      else
        return checkDataBeforeRendering(
          query.jurisdiction,
          <Chip onDelete={() => onRemoveSearchTag('jurisdiction')} label={query.jurisdiction} size="small" />,
        );
    };

    const companyName = checkDataBeforeRendering(
      query.company_name,
      <Chip onDelete={() => onRemoveSearchTag('company_name')} label={'Company Name'} size="small" />,
    );

    const companyType = checkDataBeforeRendering(
      query.company_type,
      <Chip onDelete={() => onRemoveSearchTag('company_type')} label={'Company Type'} size="small" />,
    );

    const companyNumber = checkDataBeforeRendering(
      query.company_code,
      <Chip onDelete={() => onRemoveSearchTag('company_code')} label={'Company Number'} size="small" />,
    );

    const city = checkDataBeforeRendering(
      query.city,
      <Chip onDelete={() => onRemoveSearchTag('city')} label={'City'} size="small" />,
    );

    const state = checkDataBeforeRendering(
      query.state,
      <Chip onDelete={() => onRemoveSearchTag('state')} label={'State'} size="small" />,
    );

    const incorporationDate = checkDataBeforeRendering(
      query.incorporation_date,
      <Chip label={'Incorporation Date'} onDelete={() => onRemoveSearchTag('incorporation_date')} size="small" />,
    );

    const toDate = checkDataBeforeRendering(
      query.to_date,
      <Chip onDelete={() => onRemoveSearchTag('to_date')} label={'To Date'} size="small" />,
    );

    const contactAvailable = checkDataBeforeRendering(
      query.contact_person_available,
      <Chip onDelete={() => onRemoveSearchTag('contact_person_available')} label={'Contact Available'} size="small" />,
    );

    const companyProfileIncludes = checkDataBeforeRendering(
      query.company_profile_data_includes,
      <Chip
        onDelete={() => onRemoveSearchTag('company_profile_data_includes')}
        label={'Company Profile Includes'}
        size="small"
      />,
    );

    return (
      <Stack mb={0.5} direction={'row'} gap={0.5} flexWrap={'wrap'}>
        {renderStatus()} {companyType} {companyName} {renderListJurisdictions()} {companyNumber} {city} {state}
        {incorporationDate}
        {toDate}
        {contactAvailable}
        {companyProfileIncludes}
      </Stack>
    );
  };

  return (
    <Grid alignItems={'end'} spacing={3} container>
      <Grid sm={12} md={8} lg={8} xl={9} item>
        {renderSearchTags()}
        <TextField
          sx={{ flex: 1 }}
          placeholder="Company Name, Company Number, Company Address"
          size="small"
          fullWidth
          value={defaultQuery}
          onChange={(e) => {
            onChangeSearch(e);
            setDefaultQuery(e.target.value);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <IconFeather icon={<Search />} muiProps={{ color: 'disabled' }} />
              </InputAdornment>
            ),
          }}
        />
      </Grid>
      <Grid sm={12} md={4} lg={4} xl={3} item>
        <Button
          sx={{ mr: 1 }}
          onClick={() => {
            handleOpenSearch(FormType.SearchCompany);
          }}
          color="primary"
          size="large"
          startIcon={<IconFeather muiProps={{ fontSize: 'small' }} icon={<Sliders />} />}
          variant="outlined"
        >
          Advanced Filter
        </Button>
      </Grid>
    </Grid>
  );
};

export default FilterCompanyProfile;
